/**
 * Service worker registration and the ADR-0008 §5 update handshake.
 *
 * `main.tsx` calls `registerServiceWorker()` after `window`'s `load` event, so
 * registration never competes with first paint on the audience this PWA
 * targets (mid-range Android phones on mobile data — `.claude/skills/pwa-shell/SKILL.md`).
 *
 * The handshake this module implements:
 *
 *   new SW installs → app detects `waiting` → `subscribeUpdateAvailable`
 *   fires → (a future component renders a non-blocking "new version, reload")
 *   → ONLY on the customer's confirmation, `applyUpdate()` → skipWaiting +
 *   clients.claim (the worker does the claim; see `public/sw.js`) → reload.
 *
 * 🚫 Nothing in this module ever calls `skipWaiting` on its own. That is the
 * exact trap this handshake exists to prevent: swapping the running app's
 * asset set out from under a customer mid-form. `applyUpdate()` is the only
 * path to it, and it is meant to be wired to an explicit UI confirmation —
 * this milestone builds the mechanism and its signal, not the prompt itself
 * (see `docs/handoff-notifications-milestone.md` §M4 criterion 10).
 *
 * Most of what happens here is side-effecting browser API calls, which are
 * not meaningfully unit-testable. The one decidable piece — "does this
 * registration already have an update waiting" — is a pure predicate,
 * exported and tested on its own.
 */

/** The minimal shape `hasWaitingUpdate` needs from a `ServiceWorkerRegistration`. */
export interface WaitingRegistration {
  readonly waiting: unknown;
}

/**
 * Pure: does this registration already have a worker sitting in `waiting`?
 *
 * Covers the case where this tab loads *after* another tab already finished
 * installing an update — the new worker is waiting before this tab's
 * `updatefound` listener could ever fire, so that event alone is not enough;
 * the registration must also be checked once, right after it resolves.
 */
export function hasWaitingUpdate(registration: WaitingRegistration | null | undefined): boolean {
  return registration != null && registration.waiting != null;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let updateAvailable = false;
let currentRegistration: ServiceWorkerRegistration | null = null;
let reloadingOnControllerChange = false;

/** How often a foregrounded session re-checks /sw.js. Long enough to be free, short enough that a deploy reaches an open tab the same hour. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

function markUpdateAvailable(registration: ServiceWorkerRegistration): void {
  currentRegistration = registration;
  if (updateAvailable) return;
  updateAvailable = true;
  notifyListeners();
}

/**
 * Subscribe to the "an update is waiting" signal. Returns an unsubscribe
 * function. A future component (e.g. a toast) calls this to know when to
 * render the "new version — reload" prompt described in ADR-0008 §5.
 */
export function subscribeUpdateAvailable(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Point-in-time snapshot. Safe to call any time; never triggers a prompt or a network call. */
export function isUpdateAvailable(): boolean {
  return updateAvailable;
}

/**
 * Apply the waiting update: tell the waiting worker to skip waiting, then
 * reload once it takes control.
 *
 * Call this ONLY from the customer's explicit confirmation of the "new
 * version available" prompt. It is a no-op if no update is currently waiting.
 */
export function applyUpdate(): void {
  const waiting = currentRegistration?.waiting;
  if (!waiting) return;

  if (!reloadingOnControllerChange) {
    reloadingOnControllerChange = true;
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.location.reload();
      },
      { once: true },
    );
  }

  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Register `/sw.js` and start watching for updates.
 *
 * Call once, after `window.load`. A browser without `serviceWorker` support,
 * or a registration that fails outright, degrades to "no push, no update
 * handshake" — the app shell must still render either way, so failures here
 * are swallowed rather than thrown.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  void navigator.serviceWorker
    .register('/sw.js')
    .then((registration) => {
      currentRegistration = registration;

      // Another tab may have already finished installing an update before
      // this tab's registration resolved.
      if (hasWaitingUpdate(registration)) {
        markUpdateAvailable(registration);
      }

      // ⚠️ WITHOUT THIS THE HANDSHAKE NEVER FIRES ON AN INSTALLED PWA.
      //    The browser only re-fetches /sw.js on a navigation or a cold
      //    relaunch. An installed app that lives in the iOS app switcher for
      //    days does neither, so it never learns a new worker exists — and the
      //    old one keeps controlling the page. That is not cosmetic: a stale
      //    worker has no `push` handler, so pushes arrive, the push service
      //    answers 2xx, the sender records `sent`, and the device shows
      //    nothing. Diagnosed 2026-07-30; it took the whole delivery chain to
      //    rule out because every layer reported success.
      //
      //    `update()` is a conditional fetch — the browser honours HTTP
      //    caching on /sw.js, which Caddy serves `no-cache`, so this is a
      //    revalidation, not a download.
      const checkForUpdate = (): void => {
        void registration.update().catch(() => {
          // Offline, or the fetch failed: nothing to do. The next foreground
          // tries again, and the app shell must keep working either way.
        });
      };

      // Foreground is the moment that matters: it is the only signal an
      // installed PWA reliably produces between launches.
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkForUpdate();
        });
      }

      // Belt and braces for a session left open in the foreground for hours.
      setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;

        installing.addEventListener('statechange', () => {
          // 'installed' while a controller already exists means this is a
          // NEW version replacing a running one, now sitting in `waiting` —
          // not the very first install, which has no controller yet and
          // activates on its own with nothing to confirm.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            markUpdateAvailable(registration);
          }
        });
      });
    })
    .catch(() => {
      // Swallowed deliberately — see the doc comment above.
    });
}
